import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { rentalAPI } from '../lib/rentalAPI';
import { clientsAPI } from '../lib/clientsAPI';

export default function NewRentalProperty() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    property_address: '',
    ownership_percentage: 100,
    purchase_date: '',
    settlement_date: '',
    purchase_price: '',
    land_value: '',
    property_type: 'residential',
    rental_start_date: '',
    property_manager_name: '',
    property_manager_contact: '',
    ownership_structure: 'individual'
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list();
      setClients(response.data || []);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.property_address) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        client_id: formData.client_id,
        property_address: formData.property_address,
        ownership_percentage: parseFloat(formData.ownership_percentage),
        purchase_date: formData.purchase_date || undefined,
        settlement_date: formData.settlement_date || undefined,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : undefined,
        land_value: formData.land_value ? parseFloat(formData.land_value) : undefined,
        property_type: formData.property_type,
        rental_start_date: formData.rental_start_date || undefined,
        property_manager_name: formData.property_manager_name || undefined,
        property_manager_contact: formData.property_manager_contact || undefined,
        ownership_structure: formData.ownership_structure
      };
      
      const response = await rentalAPI.createProperty(payload);
      toast.success('Rental property added successfully');
      navigate(`/rental-properties/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6"><div className="animate-pulse h-96 bg-muted rounded"></div></div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Add Rental Property
          </h1>
          <p className="text-muted-foreground mt-2">Enter property details and ownership information</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="client">Client *</Label>
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                    <SelectTrigger data-testid="client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="address">Property Address *</Label>
                  <Input
                    id="address"
                    value={formData.property_address}
                    onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                    placeholder="123 Main St, Sydney NSW 2000"
                    data-testid="address-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Property Type</Label>
                  <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="airbnb">Airbnb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Ownership %</Label>
                  <Input
                    type="number"
                    value={formData.ownership_percentage}
                    onChange={(e) => setFormData({...formData, ownership_percentage: e.target.value})}
                    placeholder="100"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Purchase Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                    placeholder="750000.00"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/rental-properties')}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Property'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}